/**
 * Qard Hasan brand marks.
 *
 * ## Why the logo is SVG rather than the supplied image
 *
 * The mark appears in the header of every screen, at 28–40px, on phones with
 * varying pixel densities and often on a poor connection. A raster logo would be
 * blurry on some of them, sharp-but-heavy on others, and a separate network
 * request on all of them. Drawn as SVG it is a few hundred bytes inline, sharp at
 * any size, and can pick up currentColor so it works on light and dark grounds
 * without a second file.
 *
 * The photograph in the original artwork is kept for the *banner*, where the image
 * is doing emotional work that a vector cannot. See `BrandBanner`.
 *
 * ## The arch
 *
 * A mihrab silhouette — the pointed arch of a prayer niche. Two quadratic curves
 * meeting at a corner give the apex its point; a single curve would round it off
 * and lose the reference entirely.
 */

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/** The pointed-arch outline, shared by the mark and the photo clip. */
const ARCH_PATH = 'M6 140 L6 58 Q6 20 50 4 Q94 20 94 58 L94 140 Z'

/**
 * A second arch inset within the first.
 *
 * This replaced an earlier attempt that put a crescent and star inside the arch.
 * Two reasons it had to go. It was illegible: at the 28px the header uses, that
 * much detail collapses into a smudge. And it was invented — the supplied artwork
 * frames a *photograph* of a mosque, and turning the incidental finial in that
 * photo into an explicit icon would be adding religious iconography to someone
 * else's brand on my own initiative.
 *
 * A nested arch says "architectural" without asserting anything, and it still
 * reads as a distinct shape at favicon size.
 */
const INNER_ARCH_PATH = 'M24 140 L24 64 Q24 38 50 26 Q76 38 76 64 L76 140 Z'

/**
 * The arch mark on its own — for the app header, where space is tight and the
 * wordmark would crowd out the page title.
 */
export function QardHasanMark({
  className = 'size-8',
  variant = 'solid',
}: {
  className?: string
  /** `solid` for light grounds, `inverse` for navy ones. */
  variant?: 'solid' | 'inverse'
}) {
  const gradientId = `qh-arch-${variant}`

  return (
    <svg
      viewBox="0 0 100 140"
      className={className}
      role="img"
      aria-label="Qard Hasan"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          {variant === 'inverse' ? (
            <>
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#c4cae9" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="#4a58a8" />
              <stop offset="100%" stopColor="#1a2156" />
            </>
          )}
        </linearGradient>
      </defs>

      <path d={ARCH_PATH} fill={`url(#${gradientId})`} />

      {/* The inner arch, in gold. A stroke rather than a fill: at small sizes a
          filled shape would swallow the navy and the mark would lose its outline. */}
      <path
        d={INNER_ARCH_PATH}
        fill="none"
        stroke={variant === 'inverse' ? '#e5b93c' : '#e5b93c'}
        strokeWidth="9"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * The full lock-up: arch, wordmark, gold rule, tagline.
 *
 * `Qard` light against `Hasan` bold is the wordmark's defining feature, so the
 * weight contrast is explicit rather than left to a font stack that may not ship
 * a 300 weight.
 */
export function QardHasanLogo({
  variant = 'solid',
  showTagline = true,
  className,
}: {
  variant?: 'solid' | 'inverse'
  showTagline?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const inverse = variant === 'inverse'

  return (
    <div className={['flex items-center gap-4', className].filter(Boolean).join(' ')}>
      <QardHasanMark className="size-14 shrink-0" variant={variant} />

      <div className="min-w-0">
        <p
          className={[
            'font-display text-2xl leading-none',
            inverse ? 'text-white' : 'text-brand-800',
          ].join(' ')}
        >
          <span className="font-normal">Qard</span>
          <span className="font-bold"> Hasan</span>
        </p>

        {showTagline && (
          <>
            {/* The gold rule from the artwork. Short and left-aligned to the
                wordmark, which is what keeps the lock-up feeling deliberate. */}
            <div className="mt-2 h-[3px] w-14 rounded-full bg-gold-500" />
            <p
              className={[
                'brand-tagline mt-2',
                inverse ? 'text-gold-300' : 'text-stone-500',
              ].join(' ')}
            >
              {t('app.logoTagline')}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The hero banner.
 *
 * ## The photograph
 *
 * Drop a mosque photograph at `public/brand/banner.jpg` and it appears behind the
 * navy wash. Without it the gradient stands on its own and looks intentional
 * rather than broken — which matters, because a hero that renders as a grey box
 * with a missing-image icon is worse than one that never had a photo.
 *
 * The navy overlay is not decoration: white text over an unmodified photograph
 * fails contrast wherever the sky is bright, and this banner carries the fund's
 * name and its central promise.
 */
export function BrandBanner({
  title,
  tagline,
  children,
}: {
  title: string
  tagline: string
  children?: ReactNode
}) {
  return (
    <div className="relative -mx-4 overflow-hidden bg-brand-800 px-4 py-10 sm:mx-0 sm:rounded-[--radius-card] sm:px-8">
      <div
        aria-hidden="true"
        className="landing-hero-orb -top-8 -right-6 size-36 bg-gold-500/30"
        style={{ animationDelay: '0s' }}
      />
      <div
        aria-hidden="true"
        className="landing-hero-orb bottom-0 -left-10 size-44 bg-brand-400/25"
        style={{ animationDelay: '2s' }}
      />
      {/* The photo, if present. `background-image` rather than <img> so a missing
          file degrades to nothing at all instead of a broken-image glyph. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-45 motion-safe:animate-[fade-in_1.2s_ease-out]"
        style={{ backgroundImage: "url('/brand/banner.jpg')" }}
      />
      {/* Wash: guarantees the text contrast regardless of what the photo does. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-brand-900/92 via-brand-800/88 to-brand-600/75"
      />

      <div className="relative flex flex-col gap-3 motion-safe:animate-[fade-in-up_0.7s_ease-out]">
        <h1 className="text-3xl leading-tight font-bold text-white sm:text-4xl">{title}</h1>
        <div
          aria-hidden="true"
          className="h-0.5 w-16 origin-left rounded-full bg-gold-400 motion-safe:animate-[expand-rule_0.8s_ease-out_0.2s_both]"
        />
        <p className="brand-tagline landing-shimmer-text">{tagline}</p>
        {children && <div className="mt-3 flex flex-col gap-3">{children}</div>}
      </div>
    </div>
  )
}
